import { DateTime } from 'luxon'
import type { MetricCoverage } from '@dashboard/modules/segment/services/questionReportingService'
import type { DateRange } from '@dashboard/services/date'
import type { FlowInsights, NodeMetrics } from './flowMetricsService'

type NodeMetricsMap = Record<string, NodeMetrics> | null | undefined

export function questionDropOffSummary({
  dropOffRate,
  droppedCount,
  visitorCount
}: NonNullable<FlowInsights['highestDropOffQuestion']>): string {
  return `${dropOffRate.toFixed(1)}% stopped here (${droppedCount.toLocaleString()} of ${visitorCount.toLocaleString()} people asked)`
}

export function peopleCount(count: number): string {
  return `${count.toLocaleString()} ${count === 1 ? 'person' : 'people'}`
}

/**
 * Why a count reads as a dash: the day counting started when the range begins earlier, else the server's reason.
 * Counting starts partway through that local day, so only a range starting the day after is fully covered.
 */
export function coverageNote(coverage: MetricCoverage | null | undefined, timezone?: string): string | undefined {
  if (coverage?.status !== 'unavailable') {
    return undefined
  }
  const from = coverage.from ? DateTime.fromISO(coverage.from, timezone ? { zone: timezone } : {}) : null
  return from?.isValid
    ? `Counted from ${from.toFormat('LLL d, yyyy')}. Choose a range that starts after that day.`
    : (coverage.reason ?? undefined)
}

/** Why unmeasured question drop-offs are missing from the list: the first ask node whose coverage is unavailable. */
export function dropOffCoverageNote(nodeMetrics: NodeMetricsMap, timezone?: string): string | undefined {
  const unmeasured = Object.values(nodeMetrics ?? {}).find(
    (node) => node.nodeType === 'sg' && node.coverage?.status === 'unavailable'
  )
  return coverageNote(unmeasured?.coverage, timezone)
}

/**
 * An ask node whose asks weren't recorded for the range: tags before display tracking never send `question_shown`,
 * so the server returns null asks with unavailable coverage. Canvas stats and the heat map show it as not measured,
 * never as zero traffic. Coverage decides it; a missing count with no coverage still reads as zero.
 */
export function asksUnmeasured(
  metrics: Partial<Pick<NodeMetrics, 'nodeType' | 'coverage'>> | null | undefined
): boolean {
  return metrics?.nodeType === 'sg' && metrics.coverage?.status === 'unavailable'
}

/** When the flow's asks started counting, for the canvas notice: a capture instant, or never (no publish since). */
export type AsksCoverage = { kind: 'from'; from: string } | { kind: 'never' }

export function asksCoverage(nodeMetrics: NodeMetricsMap): AsksCoverage | null {
  let from: string | null = null
  let unmeasured = false
  for (const node of Object.values(nodeMetrics ?? {})) {
    if (!asksUnmeasured(node)) {
      continue
    }
    unmeasured = true
    const start = node.coverage?.from
    if (start && (from === null || Date.parse(start) > Date.parse(from))) {
      from = start
    }
  }
  if (from !== null) {
    return { kind: 'from', from }
  }
  return unmeasured ? { kind: 'never' } : null
}

/** The notice's sentence and, when counting has started, the button that moves the range start to it. */
export function asksCoverageCopy(coverage: AsksCoverage, timezone?: string): { message: string; action?: string } {
  if (coverage.kind === 'never') {
    return { message: 'Publish once to start counting asks.' }
  }
  const from = DateTime.fromISO(coverage.from, timezone ? { zone: timezone } : {})
  return {
    message: `Asks are counted from ${from.toFormat('LLL d, yyyy')}, so ranges that start earlier show completions only.`,
    action: `Show from ${from.toFormat('LLL d')}`
  }
}

/**
 * The range that starts when asks started counting. It starts one second after the reported instant, not at its local
 * midnight: the server covers a range only when it starts at or after capture, and it reports capture truncated to the
 * second while the activation journal it can come from keeps microseconds. The end stays unless it falls before the
 * new start.
 */
export function rangeFromAsksCapture<T extends DateRange>(range: T, from: string, now: Date = new Date()): T {
  const start = new Date(Date.parse(from) + 1000)
  const end = range.end.getTime() > start.getTime() ? range.end : now
  return { ...range, start, end }
}
